/**
 * @module linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/realignMetricsOrder
 * @description column_sort 指区顺序对齐
 */

import { SortableType } from '@dbl/core/chartEditor';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { fieldKey, snapshotList } from '../../../fieldSnapshot';
import { isPercentageColumn } from '../untils/fieldUtils';
import { isDimPctColumnForMetric } from '../untils/dimensionPercentage';
import {
  getColumnSortItemId,
  isCompareUserFilterColumn,
  normalizeColumnSortItem,
} from '../untils/keys';
import type { IColumnSortOpsContext } from './syncOps';
import { updateColumnSortInList } from './syncOps';
import {
  MAX_ALIGN_ITERATIONS,
  dedupeList,
  findMetricSnapshot,
  getColumnSortOrderSignature,
  getMetricReorderKey,
  isMainMetricItem,
  isQBDimensionColumn,
  listMainMetricItemsInOrder,
  mergeSnapshotWithSortItem,
} from './realignColumnSortShared';

function desiredMetricIdsInQBOrder(
  list: IColumnSortItem[],
  columnMetrics: unknown[],
): string[] {
  const present = new Set(
    listMainMetricItemsInOrder(list).map(item => getMetricReorderKey(item)),
  );
  const ids: string[] = [];
  for (const snap of snapshotList(columnMetrics, 'metric')) {
    const id = fieldKey(snap);
    if (present.has(id)) {
      ids.push(id);
    }
  }
  return ids;
}

function currentMetricIdsInListOrder(list: IColumnSortItem[]): string[] {
  return listMainMetricItemsInOrder(list).map(item => getMetricReorderKey(item));
}

function metricOrderMatchesQB(
  list: IColumnSortItem[],
  columnMetrics: unknown[],
): boolean {
  const desired = desiredMetricIdsInQBOrder(list, columnMetrics);
  const current = currentMetricIdsInListOrder(list);
  if (desired.length !== current.length) {
    return false;
  }
  return desired.every((id, i) => id === current[i]);
}

function extractMetricGroupForMain(
  list: IColumnSortItem[],
  mainItem: IColumnSortItem,
): { group: IColumnSortItem[]; rest: IColumnSortItem[] } {
  const metricName = mainItem.column || '';
  const targetKey = getColumnSortItemId(mainItem);
  const group: IColumnSortItem[] = [];
  const rest: IColumnSortItem[] = [];
  list.forEach(raw => {
    const item = normalizeColumnSortItem(raw);
    if (
      item.builder_type === SortableType.Metric &&
      (getColumnSortItemId(item) === targetKey ||
        isPercentageColumn(item, metricName) ||
        isDimPctColumnForMetric(item, metricName))
    ) {
      group.push(item);
    } else {
      rest.push(item);
    }
  });
  return { group, rest };
}

function hasLeadingMetricsBeforeFirstDimension(
  list: IColumnSortItem[],
): boolean {
  let cgIndex = -1;
  let firstMetric = -1;
  let firstDim = -1;
  list.forEach((raw, index) => {
    const item = normalizeColumnSortItem(raw);
    if (isCompareUserFilterColumn(item.column) && cgIndex < 0) {
      cgIndex = index;
    }
    if (isMainMetricItem(item) && firstMetric < 0) {
      firstMetric = index;
    }
    if (isQBDimensionColumn(item) && firstDim < 0) {
      firstDim = index;
    }
  });
  if (firstMetric < 0 || firstDim < 0 || firstMetric >= firstDim) {
    return false;
  }
  return cgIndex >= 0 && cgIndex < firstMetric;
}

function shouldConsolidateLeadingMetricsBeforeDimensions(
  list: IColumnSortItem[],
  columnMetrics: unknown[],
): boolean {
  if (!hasLeadingMetricsBeforeFirstDimension(list)) {
    return false;
  }
  const metricsInList = listMainMetricItemsInOrder(list);
  const qbMetrics = snapshotList(columnMetrics, 'metric');
  if (!metricsInList.length || !qbMetrics.length) {
    return false;
  }
  const firstKey = getMetricReorderKey(metricsInList[0]);
  const lastQbKey = fieldKey(qbMetrics[qbMetrics.length - 1]);
  return firstKey === lastQbKey;
}

export function consolidateLeadingMetricsBeforeDimensions(
  list: IColumnSortItem[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  const columnMetrics = ctx.columnMetrics || [];
  if (!shouldConsolidateLeadingMetricsBeforeDimensions(list, columnMetrics)) {
    return list;
  }

  let next = dedupeList(list);
  let guard = 0;

  while (guard < MAX_ALIGN_ITERATIONS) {
    guard += 1;
    const firstDimIdx = next.findIndex(item =>
      isQBDimensionColumn(normalizeColumnSortItem(item)),
    );
    if (firstDimIdx < 0) {
      break;
    }
    const leadIdx = next.findIndex(
      (item, index) =>
        index < firstDimIdx && isMainMetricItem(normalizeColumnSortItem(item)),
    );
    if (leadIdx < 0) {
      break;
    }

    const mainItem = normalizeColumnSortItem(next[leadIdx]);
    const { group, rest } = extractMetricGroupForMain(next, mainItem);
    let lastDimInRest = -1;
    rest.forEach((item, index) => {
      if (isQBDimensionColumn(item)) {
        lastDimInRest = index;
      }
    });
    const insertPos = lastDimInRest + 1;
    const merged = [...rest];
    merged.splice(insertPos, 0, ...group);
    if (
      getColumnSortOrderSignature(merged) === getColumnSortOrderSignature(next)
    ) {
      break;
    }
    next = merged;
  }

  return next;
}

export function alignMetricGroupsToQBOrder(
  list: IColumnSortItem[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  const columnMetrics = ctx.columnMetrics || [];
  if (!columnMetrics.length) {
    return list;
  }

  let next = dedupeList(list);
  let guard = 0;

  while (
    !metricOrderMatchesQB(next, columnMetrics) &&
    guard < MAX_ALIGN_ITERATIONS
  ) {
    guard += 1;
    const desired = desiredMetricIdsInQBOrder(next, columnMetrics);
    const current = currentMetricIdsInListOrder(next);

    let targetIndex = -1;
    for (let i = 0; i < desired.length; i += 1) {
      if (desired[i] !== current[i]) {
        targetIndex = i;
        break;
      }
    }
    if (targetIndex < 0) {
      break;
    }

    const targetId = desired[targetIndex];
    const targetItem = listMainMetricItemsInOrder(next).find(
      item => getMetricReorderKey(item) === targetId,
    );
    if (!targetItem) {
      break;
    }

    const snap = findMetricSnapshot(targetItem, columnMetrics);
    if (!snap) {
      break;
    }

    const reorder = {
      field: mergeSnapshotWithSortItem(snap, targetItem),
      oldIndex: current.indexOf(targetId),
      newIndex: targetIndex,
    };

    const updated = updateColumnSortInList(next, reorder, 'metric', ctx);
    if (
      getColumnSortOrderSignature(updated) === getColumnSortOrderSignature(next)
    ) {
      break;
    }
    next = updated;
  }

  return next;
}

export function realignMetricsToQBOrder(
  list: IColumnSortItem[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  let next = dedupeList(list);
  next = consolidateLeadingMetricsBeforeDimensions(next, ctx);
  next = alignMetricGroupsToQBOrder(next, ctx);
  return next;
}
